// VideoReveal.js
//
// A blurred still that becomes a YouTube player only once the viewer asks for
// it. Two reasons the iframe is not mounted up front:
//   - a YouTube embed pulls well over a megabyte of player script on sight
//   - the blur keeps the surprise closed until someone chooses to open it
//
// Used by both the home page video section and the /baby-reveal page, so the
// lazy-mount behaviour exists in one place rather than two.
//
// Requires https://www.youtube-nocookie.com in the `frame-src` of the CSP in
// public/index.html — without it the browser renders "This content is blocked"
// with no console error on some versions.
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPlay } from 'react-icons/fa';
import './VideoReveal.css';

const VideoReveal = ({ youtubeId, image, imageFallback, objectPosition, title, label }) => {
  const [playing, setPlaying] = useState(false);
  const { t } = useTranslation();

  if (!youtubeId) return null;

  return (
    <div className="video-reveal">
      {playing ? (
        <iframe
          className="video-reveal-iframe"
          src={`https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&rel=0`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      ) : (
        /* A real <button>, not a clickable div, so it is reachable by keyboard
           and announced with its own name. */
        <button
          type="button"
          className="video-reveal-poster"
          onClick={() => setPlaying(true)}
          aria-label={t('babyReveal.playVideo', 'Spill av videoen')}
        >
          <img
            src={image}
            alt=""
            aria-hidden
            className="video-reveal-poster-img"
            style={objectPosition ? { objectPosition } : undefined}
            loading="lazy"
            decoding="async"
            // YouTube only generates maxresdefault for videos uploaded above a
            // certain resolution; it 404s otherwise. Fall back once, and guard
            // against a loop if the fallback fails too.
            onError={e => {
              if (imageFallback && e.currentTarget.src !== imageFallback) {
                e.currentTarget.src = imageFallback;
              }
            }}
          />
          <span className="video-reveal-poster-scrim" aria-hidden />
          <span className="video-reveal-play" aria-hidden>
            <FaPlay />
          </span>
          {label ? <span className="video-reveal-poster-label">{label}</span> : null}
        </button>
      )}
    </div>
  );
};

export default VideoReveal;
